import React, { ChangeEvent, useEffect, useState } from 'react'
import { getRequest, patchRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { Info } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { Switch, Typography, Grid } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { EtdProviderConfigItem, ShipRocketType, ShipRocketErrorType } from 'src/lib/types/integrations'
import {
  defaultShipRocketConfig,
  defaultShipRocketErrorState,
  handleErrors,
  parseShipRocketConfig,
  parseSavedShipRocketConfig,
  parseShipRocketConfigForBackend,
  getInputType,
  getCharacterLimit,
  getMerchantAttributesPayload
} from 'src/lib/utils/integrationsHelpers'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { setMerchantAttributes } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useLocale } from 'src/lib/hooks/useLocal'

const ShipRocket = () => {
  const { integrations, setIntegrations } = useIntegrations()
  const {
    getters: { merchant },
    actions: { setMerchantData }
  } = useMerchant()
  const { temporary, setTemporaryData } = useTemporary()
  const { t } = useLocale()

  const [shipRocketConfig, setShipRocketConfig] = useState<ShipRocketType>(defaultShipRocketConfig)
  const [errors, setErrors] = useState<ShipRocketErrorType>(defaultShipRocketErrorState)
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)
  const { setLoaderProperties } = useLoader()

  const fetchETDProviderData = async () => {
    try {
      const results = await Promise.allSettled([
        getRequest('/merchant-shipping-configuration', apiURI.SHIPPING_ENGINE),
        getRequest('/utility/external-etd-config-providers/SHIP_ROCKET/config', apiURI.SHIPPING_ENGINE)
      ])
      results.forEach(result => {
        if (result.status === 'rejected') {
          throw new Error(result.reason)
        }
      })
      const parsedData = parseShipRocketConfig(results)
      setShipRocketConfig(parsedData)
      setIntegrations({
        ...integrations,
        shipRocket: parsedData
      })
    } catch (error) {
      errorToast('Error fetching data')
    }
  }

  const saveShipRocketConfig = async () => {
    const { errors, hasErrors } = handleErrors(shipRocketConfig)
    setErrors(errors)
    if (hasErrors) return
    setLoaderProperties({ isLoaderVisible: true })
    try {
      const response: any = await patchRequest(
        '/merchant-shipping-configuration',
        apiURI.SHIPPING_ENGINE,
        parseShipRocketConfigForBackend(shipRocketConfig)
      )
      if (response?.error) {
        throw new Error(response?.error)
      }
      if (!shipRocketConfig.enabled) {
        const payload = getMerchantAttributesPayload(merchant)
        const response: any = await setMerchantAttributes(payload, merchant?.accountId ?? '')
        setMerchantData(response?.data)
      }
      const newConfig = parseSavedShipRocketConfig(response, shipRocketConfig.shipRocketUIFields)
      setIntegrations({
        ...integrations,
        shipRocket: newConfig
      })
      successToast('Changes saved')
    } catch (e: any) {
      errorToast(e.message ?? 'Error saving merchant ETD config')
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }
  useEffect(() => {
    fetchETDProviderData()
  }, [])

  useEffect(() => {
    if (!temporary) return
    setShipRocketConfig(temporary.integrations?.shipRocket)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  useEffect(() => {
    setSettingsUpdated(Boolean(JSON.stringify(integrations.shipRocket) !== JSON.stringify(shipRocketConfig)))
  }, [integrations.shipRocket, shipRocketConfig])

  useEffect(() => {
    if (!integrations.shipRocket) return
    setShipRocketConfig(integrations.shipRocket)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [integrations.shipRocket, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            shipRocket: shipRocketConfig
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const handleDiscardSaving = () => {
    if (!integrations.shipRocket) return
    setShipRocketConfig(integrations.shipRocket)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  return (
    <>
      <LayoutContainer>
        <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>ShipRocket is </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${shipRocketConfig?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='ShipRocketToggle'
                checked={shipRocketConfig?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const checked = event?.target?.checked
                  setShipRocketConfig({ ...shipRocketConfig, enabled: checked })
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <div className='mt-1'>
          <h3 className='text-[16px] font-medium mb-4'>Getting Started</h3>
          <Typography variant='body2' component='div'>
            <ol className='list-decimal pl-4 space-y-2'>
              <li>
                <span>First, you need to register with ShipRocket and create an account. Click on this</span>
                <a href='https://app.shiprocket.in/register' target='_blank' rel='noopener noreferrer'>
                  <span className='text-[#C7981F]'>link</span>
                </a>
                <span>to complete the sign-up process.</span>
              </li>

              <li>
                After sign-up, follow these steps to create an API user:
                <ul className='list-disc pl-4 mt-1 space-y-1'>
                  <li>Open your Shiprocket account and navigate to the left-hand panel</li>
                  <li>Click On Settings =&gt; API =&gt; Configure =&gt; Create an API user</li>
                  <li>
                    On the pop-up menu, fill in the email (this should be different from the one used to register to
                    ShipRocket)
                  </li>
                  <li>Fill in an appropriate password and confirm the password</li>
                  <li>Click on 'Generate API Credential'</li>
                </ul>
              </li>
              <li>On successful completion, you should have a new API user generated</li>
              <li>
                Use the Authentication API to obtain an authentication token. Copy this token and use it to authenticate
                your further API calls
              </li>
            </ol>
          </Typography>
          <p className='text-[14px] mt-5 '>
            <a href='https://apidocs.shiprocket.in/#intro' target='_blank' rel='noopener noreferrer'>
              <span className='text-[#C7981F]'>Click here</span>
            </a>
            <span>to learn more about the API authentication process.</span>
          </p>
        </div>
      </LayoutContainer>

      <LayoutContainer heading='Credentials'>
        <div className='w-full md:w-full px-2 py-3'>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-[10px] border-[1px] border-[#EFD08080] text-[#C7981F] rounded-lg w-fit px-[10px] py-2 bg-[#EFD0801A]'>
              <Info size={16} color='#C7981F' />
              <p className='text-[14px]'>{t('shipping_info')}</p>
            </div>
            {shipRocketConfig.shipRocketUIFields.map((item: EtdProviderConfigItem, index: number) => (
              <InputTextField
                key={item.name}
                textFieldId={index.toString()}
                label={item.label}
                isRequired={item.required}
                inputType={getInputType(item.name)}
                autoComplete='new-password'
                inputValue={shipRocketConfig[item.name as keyof ShipRocketType]}
                handleChange={e => {
                  setShipRocketConfig({ ...shipRocketConfig, [item.name]: e.target.value })
                }}
                handleFocus={() => {
                  setErrors({ ...errors, [item.name]: { error: '', errorStatus: false } })
                }}
                customErrorMessage=''
                inputProps={{
                  autoComplete: 'new-password',
                  'data-lpignore': 'true',
                  'data-form-type': 'other',
                  form: {
                    autoComplete: 'off'
                  }
                }}
                fixedCharacterLimit={getCharacterLimit(item.name)}
                errorStatus={errors[item.name as keyof typeof errors]?.errorStatus ?? false}
                errorMessage={errors[item.name as keyof typeof errors]?.error ?? ''}
              />
            ))}
          </div>
        </div>
      </LayoutContainer>

      <SaveNotification
        isVisible={settingsUpdated}
        onSave={saveShipRocketConfig}
        onDiscard={handleDiscardSaving}
        className='w-full'
      />
    </>
  )
}

export default ShipRocket
